import { z } from "zod";
import { OutboundTransportSchema } from "../schemas/EmailRoutingSchema";
const revision = z.string().regex(/^[a-f0-9]{64}$/);
const tenant = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
const safeIdentifier = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[^\u0000-\u001f\u007f]*$/);
const guid = z.string().trim().toLowerCase().uuid();
export const MicrosoftApplicationSettingsSchema = z
  .object({
    tenantId: guid,
    userEmail: z.string().trim().email().max(320),
    servicePrincipalObjectId: guid.optional(),
  })
  .strict();
const identity = {
  tenantId: safeIdentifier,
  userEmail: z.string().email().max(320),
};
export const MicrosoftConfigurationSchema = z
  .object({
    tenantId: tenant,
    revision,
    selectedTransport: OutboundTransportSchema,
    oauthAvailable: z.boolean(),
    applicationAvailable: z.boolean(),
    configuration: z
      .discriminatedUnion("authMode", [
        z
          .object({
            authMode: z.literal("APPLICATION"),
            ...identity,
            servicePrincipalObjectId: safeIdentifier.optional(),
            rbacEvidence: z.discriminatedUnion("kind", [
              z
                .object({
                  kind: z.literal("ADMIN_ASSERTION"),
                  assertedAt: z.string().datetime(),
                })
                .strict(),
              z.object({ kind: z.literal("LEGACY_UNVERIFIED") }).strict(),
            ]),
          })
          .strict(),
        z
          .object({
            authMode: z.literal("DELEGATED"),
            ...identity,
            credentialStored: z.literal(true),
          })
          .strict(),
      ])
      .nullable(),
  })
  .strict();
export const ConfigureMicrosoftCommandSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("SAVE_APPLICATION"),
      expectedRevision: revision,
      settings: MicrosoftApplicationSettingsSchema,
      rbacAssertion: z.object({ confirmed: z.literal(true) }).strict(),
    })
    .strict(),
  z
    .object({ action: z.literal("DISCONNECT"), expectedRevision: revision })
    .strict(),
]);
export const MicrosoftConfigurationIssueSchema = z
  .object({
    path: z.enum([
      "settings.tenantId",
      "settings.userEmail",
      "settings.servicePrincipalObjectId",
      "rbacAssertion.confirmed",
      "form",
    ]),
    code: z.enum(["REQUIRED", "INVALID", "TOO_LONG", "ASSERTION_REQUIRED"]),
  })
  .strict();
export const BeginMicrosoftOAuthCommandSchema = z
  .object({ expectedRevision: revision })
  .strict();
const callback = z
  .string()
  .url()
  .max(2048)
  .refine((value) => {
    const url = new URL(value);
    return (
      !url.username &&
      !url.password &&
      !url.hash &&
      !url.search &&
      url.pathname === "/api/email-ops/microsoft/callback" &&
      (url.protocol === "https:" ||
        (url.protocol === "http:" &&
          ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname)))
    );
  });
export const BeginMicrosoftOAuthResultSchema = z
  .object({
    tenantId: tenant,
    revision,
    authorizationUrl: z.string().url().max(8192),
    clientId: safeIdentifier,
    redirectUri: callback,
  })
  .strict()
  .superRefine((value, ctx) => {
    const url = new URL(value.authorizationUrl);
    const expected: Record<string, string> = {
      client_id: value.clientId,
      redirect_uri: value.redirectUri,
      response_type: "code",
      response_mode: "query",
      scope: "openid profile email offline_access Mail.Send",
      code_challenge_method: "S256",
      prompt: "select_account",
    };
    let valid =
      url.origin === "https://login.microsoftonline.com" &&
      url.pathname === "/organizations/oauth2/v2.0/authorize" &&
      !url.username &&
      !url.password &&
      !url.hash &&
      !url.port;
    for (const [key, item] of Object.entries(expected))
      valid =
        valid &&
        url.searchParams.getAll(key).length === 1 &&
        url.searchParams.get(key) === item;
    for (const key of ["state", "nonce", "code_challenge"])
      valid =
        valid &&
        url.searchParams.getAll(key).length === 1 &&
        /^[A-Za-z0-9_-]{32,256}$/.test(url.searchParams.get(key) ?? "");
    valid = valid && Array.from(url.searchParams.keys()).length === 10;
    if (!valid)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["authorizationUrl"],
        message: "Unexpected Microsoft authorization URL",
      });
  });
export type MicrosoftConfiguration = z.infer<
  typeof MicrosoftConfigurationSchema
>;
export type MicrosoftApplicationSettings = z.infer<
  typeof MicrosoftApplicationSettingsSchema
>;
export type ConfigureMicrosoftCommand = z.infer<
  typeof ConfigureMicrosoftCommandSchema
>;
export type MicrosoftConfigurationIssue = z.infer<
  typeof MicrosoftConfigurationIssueSchema
>;
export type BeginMicrosoftOAuthCommand = z.infer<
  typeof BeginMicrosoftOAuthCommandSchema
>;
export type BeginMicrosoftOAuthResult = z.infer<
  typeof BeginMicrosoftOAuthResultSchema
>;
